"use client";

import "photoswipe/dist/photoswipe.css";
import Header11 from "@/components/header/header-11";
import Overview from "@/components/hotel-single/Overview";
import StickyHeader from "@/components/hotel-single/StickyHeader";
import TopBreadCrumb from "@/components/hotel-single/TopBreadCrumb";
import SidebarRight from "@/components/hotel-single/SidebarRight";
import ReviewProgress from "@/components/hotel-single/guest-reviews/ReviewProgress";
import CallToActions from "@/components/common/CallToActions";
import DefaultFooter from "@/components/footer/default";
import GalleryOne from "@/components/hotel-single/GalleryOne";
import HotelFacilities from "@/components/hotel-single/HotelFacilities";
import { Rooms } from "@/components/hotel-single/rooms";

import {
  getHotelLoading,
  getHotel,
  viewHotel
} from "@/lib/features/hotel/hotelSlice";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { makeQueryString, parseQueryString } from "@/utils/queryString";
import DefaultHeader from "../header/default-header";
import MainFilterSearchBox from "@/components/hotel-listing-v2/ui/SearchBar/MainFilterSearchBox";
import {
  getPlaces,
} from "@/lib/features/home/homeSlice";
import { VIEW_ITEM_EVENT, trackEvent } from "@/utils/gtm";
import { getMarkupPrice } from "@/utils/helper";
import IdleTimeoutModal from "@/components/ui/IdleTimeoutModal";
import HotelJsonLd from "@/components/seo/HotelJsonLd";
import { ROAVE_API_BASE_URL, ROAVE_SEARCH_API_BASE_URL } from "@/config/endpoints";

const HotelDetail = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const lastRecoveryAttemptRef = useRef<Map<string, number>>(new Map());
  const isRecoveringRef = useRef(false);
  const [isRecoveringSearch, setIsRecoveringSearch] = useState(false);

  const query: any = useSearchParams();

  const payloadFromQuery = useMemo(() => {
    return query ? parseQueryString(query) : null;
  }, [query]);

  
  const parsedPayload = payloadFromQuery;
  const hotelId: any = Number(parsedPayload?.hotel_code);
  const searchId = parsedPayload?.search_id;
  const hotelLoading = useAppSelector(getHotelLoading);
  const hotelData: any = useAppSelector(viewHotel);
  const hotelDetail = hotelData && hotelData[hotelId]?.hotel;

  const shouldRetryRecovery = useCallback((response: Response, payload: any) => {
    const detail = String(payload?.detail || payload?.message || "").toLowerCase();
    return (
      response.status === 529 ||
      payload?.status === 529 ||
      detail.includes("throttled") ||
      (response.status === 400 && detail.includes("something went wrong")) ||
      (payload?.status === 400 && detail.includes("something went wrong"))
    );
  }, []);

  const recoverExpiredSearch = useCallback(async () => {
    if (!parsedPayload || !hotelId || isRecoveringRef.current) {
      return false;
    }

    try {
      isRecoveringRef.current = true;
      setIsRecoveringSearch(true);
      const guests = Array.isArray(parsedPayload?.guests)
        ? parsedPayload.guests
        : [{ adults: 2, children_ages: [] }];
      const rooms = guests.map((guest: any) => ({
        adults: Number(guest?.adults || 2),
        children_ages: Array.isArray(guest?.children_ages)
          ? guest.children_ages
              .map((age: any) => Number(age))
              .filter((age: number) => Number.isFinite(age))
          : [],
      }));

      const [checkin = "", checkout = ""] = Array.isArray(parsedPayload?.dates)
        ? parsedPayload.dates
        : [];
      const baseApiUrl = ROAVE_API_BASE_URL;
      const params = new URLSearchParams({
        code: String(hotelId),
        type: "hotel",
        checkin,
        checkout,
        rooms: JSON.stringify(rooms),
        rates: "comprehensive",
        currency: parsedPayload?.currency || "USD",
        nationality: parsedPayload?.nationality || "US",
      }).toString();

      const parseThrottleDelayMs = (response: Response, payload: any) => {
        const retryAfterHeader = response.headers.get("retry-after");
        const retryAfterSec = retryAfterHeader ? Number(retryAfterHeader) : NaN;
        if (Number.isFinite(retryAfterSec) && retryAfterSec > 0) {
          return retryAfterSec * 1000;
        }

        const detail = String(payload?.detail || payload?.message || "");
        const secondsMatch = detail.match(/available in\s+(\d+)\s*seconds?/i);
        if (secondsMatch?.[1]) {
          return Number(secondsMatch[1]) * 1000;
        }

        return 5000;
      };

      const sleep = (ms: number) =>
        new Promise((resolve) => setTimeout(resolve, ms));

      let result: any = null;

      for (let attempt = 0; attempt < 3; attempt++) {
        const response = await fetch(`${baseApiUrl}/v1/hotels/first-available-batch?${params}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(process.env.NEXT_PUBLIC_X_ENV ? { "X-ENV": process.env.NEXT_PUBLIC_X_ENV } : {}),
          },
        });

        result = await response.json().catch(() => null);

        const shouldRetry = shouldRetryRecovery(response, result);
        if (!shouldRetry) {
          break;
        }

        if (attempt < 2) {
          const isThrottleCase =
            response.status === 529 ||
            result?.status === 529 ||
            /throttled/i.test(String(result?.detail || result?.message || ""));
          const waitMs = isThrottleCase
            ? parseThrottleDelayMs(response, result)
            : Math.min(2000 * (attempt + 1), 5000);
          await sleep(waitMs);
        }
      }

      const nextSearchId = result?.data?.search_id;

      if (nextSearchId && nextSearchId !== searchId) {
        const nextPayload = {
          ...parsedPayload,
          hotel_code: hotelId,
          search_id: nextSearchId,
        };
        router.replace(`/hotel?${makeQueryString(nextPayload)}`);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error recovering hotel search:", error);
      return false;
    } finally {
      isRecoveringRef.current = false;
      setIsRecoveringSearch(false);
    }
  }, [hotelId, parsedPayload, router, searchId, shouldRetryRecovery]);

  useEffect(() => {
    // Create an async function inside the effect
    const fetchHotelData = async () => {
      if (hotelId && searchId) {
        const searchKey = `${hotelId}:${searchId}`;
        const now = Date.now();
        const lastAttemptAt = lastRecoveryAttemptRef.current.get(searchKey) || 0;
        const canAttemptRecovery = now - lastAttemptAt >= 8000;

        try {
          const guests = Array.isArray(parsedPayload?.guests)
            ? parsedPayload.guests
            : [{ adults: 2, children_ages: [] }];
          const rooms = guests.map((guest: any) => ({
            adults: Number(guest?.adults || 2),
            children_ages: Array.isArray(guest?.children_ages)
              ? guest.children_ages
                  .map((age: any) => Number(age))
                  .filter((age: number) => Number.isFinite(age))
              : [],
          }));
          const [checkin = "", checkout = ""] = Array.isArray(parsedPayload?.dates)
            ? parsedPayload.dates
            : [];

          const response = await dispatch(
            getHotel({
              url: "v1/hotels/details",
              payload: {
                search_id: searchId,
                hotel_code: hotelId,
                checkin,
                checkout,
                rooms: JSON.stringify(rooms),
                currency: parsedPayload?.currency || "USD",
                nationality: parsedPayload?.nationality || "US",
                rates: "comprehensive",
              },
            })
          ).unwrap();
          const { status, data } = response || {};

          if (status !== 200 || !data?.hotel) {
            if (canAttemptRecovery) {
              lastRecoveryAttemptRef.current.set(searchKey, now);
              await recoverExpiredSearch();
            }
            return;
          }
          // Handle the data here
          console.log("Data fetched View Screen:", data);
          const itemsData = {
            item_id:  data?.hotel?.hotel_code,
            item_name: data?.hotel?.name,
            markup: data?.hotel?.markup,  
            index: 0,
            item_category: data?.hotel?.accommodation_type_sub_name || "",
            location_id: data?.hotel?.city_code,
          };
          const firstRate = data?.hotel?.rates?.[0];
          if (firstRate?.price != null && firstRate?.currency) {
            const amount = getMarkupPrice(firstRate.price, data?.hotel?.markup);
            const gtmData = {
              ecommerce: {
                currency: firstRate.currency,
                value: amount,
                items: itemsData
              }
            };
            trackEvent(VIEW_ITEM_EVENT, gtmData);
          }

        } catch (error) {
          // Handle errors here
          console.error("Error fetching hotel data:", error);
          if (canAttemptRecovery) {
            lastRecoveryAttemptRef.current.set(searchKey, now);
            await recoverExpiredSearch();
          }
        }
      }
    };
  
    // Call the async function
    fetchHotelData();
  }, [hotelId, searchId, dispatch, recoverExpiredSearch, parsedPayload]); // Make sure to include all dependencies


  useEffect(() => {
    if (parsedPayload?.hotel_code) {
      // console.log("Inside useEffect parsedPayload", parsedPayload);
      dispatch(
        getPlaces({
          apiUrl: ROAVE_SEARCH_API_BASE_URL,
          url: "api/app/search",
          payload: {
            query: parsedPayload?.location?.name,
            limit: process.env.NEXT_PUBLIC_REDIS_SEARCH_LIMIT,
          },
        })
      );
    }
  }, [parsedPayload]);


  return (
    <>
      <HotelJsonLd hotel={hotelDetail} />
      {/* End Page Title */}
      <div className="header-margin"></div>
      {/* header top margin */}
      <DefaultHeader/>
      {/* <Header11 /> */}
      {/* End Header 1 */}

      <TopBreadCrumb hotel={hotelDetail} loading={hotelLoading}/>
      {/* End top breadcrumb */}
      {!hotelLoading && !hotelDetail && (
        <section className="py-20 bg-light-2">
          <div className="container">
            <div className="d-flex flex-column flex-lg-row gap-15 align-items-start align-items-lg-center justify-content-between">
              <div className="text-15 text-dark-1">
                We could not load live hotel details from this search. Please refresh availability.
              </div>
              <button
                type="button"
                onClick={recoverExpiredSearch}
                className="button h-50 px-24 -dark-1 bg-blue-1 text-white"
                disabled={isRecoveringSearch}
              >
                {isRecoveringSearch ? "Refreshing..." : "Refresh Hotel Availability"}
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="pb-5 bg-light-2">
        <div className="container">
          <div className="row">
            <div className="col-12 mt-40 mb-40">
              {/* End text-center */}
              <MainFilterSearchBox requestFrom={"hotels"} />
            </div>
            {/* End col-12 */}
          </div>
        </div>
      </section>



      <StickyHeader hotel={hotelDetail} loading={hotelLoading}/>
      {/* sticky single header for hotel single */}

      <GalleryOne hotel={hotelDetail} loading={hotelLoading} />

      {/* End gallery grid wrapper */}
      
      <section className="pt-30">
        <div className="container">
          <div className="row y-gap-30">
            <div className="col-xl-12">
              <div className="row y-gap-40">
                {/* <div className="col-12">
                  <h3 className="text-22 fw-500">Property highlights</h3>
                  <PropertyHighlights />
                </div> */}
                {/* End .col-12 Property highlights */}

                <div id="overview" className="col-12">
                  <Overview  hotel={hotelDetail} loading={hotelLoading} />
                </div>
                {/* End .col-12  Overview */}

                <div className="col-12 d-none d-sm-block" id="facilities">
                  <h3 className="text-22 fw-500 pt-40 border-top-light">
                    Most Popular Facilities
                  </h3>
                  <div className="row y-gap-10 pt-20">
                    <HotelFacilities hotel={hotelDetail} loading={hotelLoading}/>
                  </div>
                </div>
                {/* End .col-12 Most Popular Facilities */}

                {/* <div className="col-12">
                  <RatingTag />
                </div> */}
                {/* End .col-12 This property is in high demand! */}
              </div>
              {/* End .row */}
            </div>
            {/* End .col-xl-8 */}

            {/* <div className="col-xl-12">
              <SidebarRight hotel={hotelDetail} loading={hotelLoading}/>
            </div> */}
            {/* End .col-xl-4 */}
          </div>
          {/* End .row */}
        </div>
        {/* End container */}
      </section>
      {/* End single page content */}

      <section id="rooms" className="mt-30">
        <div className="container">
         
          {/* End .row */}
          {/* <AvailableRooms /> */}
          <Rooms loading={hotelLoading}/>
        </div>
        {/* End .container */}
      </section>
      {/* End Available Rooms */}

      <section className="pt-40" id="reviews">
        <div className="container">
         
         
          <ReviewProgress hotel={hotelDetail} loading={hotelLoading} />
          {/* End review with progress */}

          {/* <div className="pt-40">
            <DetailsReview />
          </div> */}

          {/* <div className="row pt-30">
            <div className="col-auto">
              <a href="#" className="button -md -outline-blue-1 text-blue-1">
                Show all 116 reviews{" "}
                <div className="icon-arrow-top-right ml-15"></div>
              </a>
            </div>
          </div> */}
          {/* End .row */}
        </div>
        {/* End .container */}
        {/* End container */}
      </section>
      {/* End Review section */}

      <CallToActions />
      {/* End Call To Actions Section */}

      <DefaultFooter />
      <IdleTimeoutModal/>
    </>
  );
};

// export default dynamic(() => Promise.resolve(HotelSingleV1Dynamic), {
//   ssr: false,
// });
export default HotelDetail;
